import { useEffect } from 'react'

// Canonical domains — single source of truth for every SEO surface
// (canonical tags, Open Graph URLs, sitemap, structured data).
export const SITE_URL = 'https://www.bassiligroup.com' // this corporate site
export const PRODUCT_URL = 'https://www.bassilichat.ai' // BassiliChat AI product (canonical; .com redirects here)
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/1.png`

interface SeoOptions {
  /** Page <title>. Keep it unique per route and under ~60 characters. */
  title: string
  /** Meta description. Unique per route, ~150–160 characters. */
  description: string
  /** Route pathname used to build the canonical/OG url, e.g. '/bassilichatai'. */
  path?: string
  /** Absolute Open Graph image URL. */
  image?: string
  /** Open Graph type (website | article | product…). */
  type?: string
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Per-route SEO for this client-rendered SPA. Updates the document title,
 * meta description, canonical link and Open Graph / Twitter tags on mount and
 * whenever the inputs change. The static defaults in index.html cover crawlers
 * that don't execute JavaScript; this hook keeps JS-rendering crawlers
 * (Googlebot) and social/link unfurlers accurate per page.
 */
export function useSeo({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
}: SeoOptions) {
  useEffect(() => {
    const url = `${SITE_URL}${path}`

    document.title = title
    upsertMeta('name', 'description', description)
    upsertLink('canonical', url)

    // Open Graph
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:url', url)
    upsertMeta('property', 'og:image', image)
    upsertMeta('property', 'og:type', type)

    // Twitter
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    upsertMeta('name', 'twitter:image', image)
  }, [title, description, path, image, type])
}
